import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Divider, Alert, CircularProgress, Switch, FormControlLabel, TextField,
} from '@mui/material';
import { Storefront as StorefrontIcon } from '@mui/icons-material';
import ApiService from '../../../services/api.service';
import { err } from './shared';
import { can } from '../../../utils/roleUtils';

const DiscoveryPage = ({ currentUser }) => {
  const canWrite = can(currentUser, 'connections', 'write');
  const [profile, setProfile] = useState({ tagline: '', description: '', specializations: '', emirates: '', discoverable: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await ApiService.getOwnB2bProfile();
      const d = p?.data?.data || {};
      setProfile({
        tagline: d.tagline || '',
        description: d.description || '',
        specializations: (d.specializations || []).join(', '),
        emirates: (d.emirates_served || []).join(', '),
        discoverable: d.discoverable === true,
      });
    } catch (e) { /* no profile yet */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await ApiService.saveB2bProfile({
        tagline: profile.tagline,
        description: profile.description,
        specializations: profile.specializations.split(',').map((s) => s.trim()).filter(Boolean),
        emirates_served: profile.emirates.split(',').map((s) => s.trim()).filter(Boolean),
        services_offered: [],
      });
      setSuccess('Profile saved');
    } catch (e) { setError(err(e, 'Failed to save profile')); }
    finally { setSaving(false); }
  };

  const toggleDiscoverable = async (val) => {
    try { await ApiService.setB2bDiscoverable(val); setProfile((p) => ({ ...p, discoverable: val })); }
    catch (e) { setError(err(e, 'Failed to update visibility')); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Discovery</Typography>
      <Typography variant="caption" color="text.secondary">
        Control whether client typing centers can find you by name, and what your public profile shows. Prices are never shown — they’re discussed offline via inquiries.
      </Typography>

      {!canWrite && <Alert severity="info" sx={{ mt: 2 }}>You have view-only access to your discovery profile.</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 3, mt: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StorefrontIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Discovery Profile</Typography>
          </Box>
          <FormControlLabel
            control={<Switch color="primary" checked={profile.discoverable} disabled={!canWrite} onChange={(e) => toggleDiscoverable(e.target.checked)} />}
            label={<Typography variant="body2">Discoverable</Typography>}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        <TextField fullWidth size="small" label="Tagline" sx={{ mb: 2 }} disabled={!canWrite}
          value={profile.tagline} onChange={(e) => setProfile((p) => ({ ...p, tagline: e.target.value }))} />
        <TextField fullWidth size="small" label="Description" multiline rows={2} sx={{ mb: 2 }} disabled={!canWrite}
          value={profile.description} onChange={(e) => setProfile((p) => ({ ...p, description: e.target.value }))} />
        <TextField fullWidth size="small" label="Specializations (comma-separated)" sx={{ mb: 2 }} disabled={!canWrite}
          value={profile.specializations} onChange={(e) => setProfile((p) => ({ ...p, specializations: e.target.value }))} />
        <TextField fullWidth size="small" label="Emirates served (comma-separated)" sx={{ mb: 2 }} disabled={!canWrite}
          value={profile.emirates} onChange={(e) => setProfile((p) => ({ ...p, emirates: e.target.value }))} />
        {canWrite && (
          <Button variant="contained" onClick={saveProfile} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default DiscoveryPage;
